import { useEffect, useState } from 'react';
import useSWR from 'swr';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { get } from '@/lib/api';
import { Thumbnail } from './Thumbnail';

// Pick an approved logo out of the logoLibrary collection for one decoration
// slot. The dialog hands back the library DOC ID only — the GCS path is
// resolved server-side by POST /api/submissions/:id/logos/:tier/from-library,
// so nothing here can aim a copy at an arbitrary object.

type Logo = {
  id: string;
  name?: string;
  schoolName?: string;
  sport?: string;
  searchTerms?: string;
  logoPath?: string;
  thumbnailPath?: string;
  usageCount?: number;
};
type Payload = { logos: Logo[] };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What the chosen logo will be applied to, e.g. "Tier 1 · Left Sleeve". */
  target: string;
  busy?: boolean;
  onConfirm: (logoId: string) => void | Promise<void>;
};

export function LogoLibraryPickerDialog({ open, onOpenChange, target, busy, onConfirm }: Props) {
  const { data, isLoading, error } = useSWR<Payload>(open ? '/api/logos?limit=200' : null, get);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    if (open) { setQ(''); setSelected(''); }
  }, [open]);

  const logos = (data?.logos ?? []).filter((l) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return [l.name, l.schoolName, l.sport, l.searchTerms]
      .some((f) => String(f || '').toLowerCase().includes(needle));
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Use a library logo</DialogTitle>
          <DialogDescription>
            Replaces the artwork on <span className="font-medium">{target}</span>. Preflight for that
            location has to be re-run afterwards.
          </DialogDescription>
        </DialogHeader>

        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, school, sport…" />

        <div className="max-h-[46vh] overflow-y-auto">
          {error ? (
            <p className="p-3 text-[11.5px] text-danger">Logo library unavailable — {String((error as Error).message || error)}</p>
          ) : isLoading ? (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-square w-full" />)}
            </div>
          ) : logos.length ? (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {logos.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setSelected(l.id)}
                  className={`flex flex-col rounded-[10px] border p-1.5 text-left transition-colors ${
                    selected === l.id ? 'border-primary bg-primary/[0.06]' : 'border-black/[0.06] hover:bg-black/[0.03]'
                  }`}
                >
                  <Thumbnail path={l.thumbnailPath || l.logoPath} alt={l.name} className="aspect-square w-full" />
                  <span className="mt-1 truncate text-[11px] font-medium" title={l.name}>{l.name || 'Untitled'}</span>
                  <span className="truncate text-[10px] text-muted">{l.schoolName || ''}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="p-3 text-[11.5px] text-muted">No library logos {q ? `match “${q}”` : 'found'}.</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" disabled={!!busy} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!selected || !!busy} onClick={() => onConfirm(selected)}>
            {busy ? 'Applying…' : 'Use this logo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
